import type { TFunction } from 'i18next'

import { describeProblem, fieldErrors } from '@/lib/problems'

/* Server refusals turned into the reader's sentence, for the directory.
 *
 * A module rather than a component file for quoteText.ts's reason: neither
 * function returns markup, and a component file may only export components.
 */

/** A refused delete or merge, read off the problem's structured members.
 *
 * THE NUMBERS ARE THE FEATURE. FR-39 makes the refusal name the referencing
 * quotes and cases, because "cannot delete, it is in use" sends somebody hunting
 * through every quote in the system. But Story 1.8's AC says the client maps the
 * problem's stable type suffix to a TRANSLATED message "rather than displaying
 * the English developer-facing detail" — and the two together mean the numbers
 * cannot be read out of `detail`, which is written server-side in English for a
 * log. A Russian user meeting a refused delete is the last person who should be
 * handed a second language.
 *
 * So the server puts them in `errors` — the same structured member Story 5.3's
 * branding refusal used for `issuingEntity: ['branding-incomplete']` — and this
 * reads them back. Returns null when the refusal is not this one, so a caller
 * falls through to the generic line rather than inventing a cause.
 */
export interface ReferencedRefusal {
  /** The referencing record numbers, already capped by the server. */
  references: string[]
  /** How many more were withheld by that cap. Zero when none were. */
  more: number
  /** The action the server offers instead: merge, or deactivate. */
  suggestion: 'merge' | 'deactivate'
}

export function referencedRefusal(error: unknown): ReferencedRefusal | null {
  const fields = fieldErrors(error)
  const references = fields['references']
  if (references === undefined) return null

  /* The suggestion decides which control is offered, so an unrecognised value
     must not silently become one of them: a transport entry offered a merge it
     has nothing to merge into is a dead end, and a client offered only
     deactivation loses FR-39's stated action. Unknown falls back to
     deactivation, the one action that always exists. */
  const offered = fields['suggestion']?.[0]
  const suggestion = offered === 'merge' ? 'merge' : 'deactivate'

  // A count the server sends only when it truncated. Absent means it did not,
  // which is a different fact from "it truncated by zero".
  const more = Number(fields['referencesMore']?.[0] ?? 0)

  return {
    references,
    more: Number.isFinite(more) && more > 0 ? more : 0,
    suggestion,
  }
}

/** The refused delete as one sentence, naming the records and the way out.
 *
 * Interpolated rather than concatenated, because Russian runs 15–30% longer and
 * puts the list in a different place in the clause (UX-DR-30). The catalogue owns
 * the sentence; this owns only which one and what goes in it.
 */
export function referencedMessage(
  refusal: ReferencedRefusal,
  t: TFunction,
): string {
  const named = refusal.references.join(', ')
  const list =
    refusal.more > 0
      ? t('directories.referenced.andMore', { named, more: refusal.more })
      : named

  return refusal.suggestion === 'merge'
    ? t('directories.referenced.mergeInstead', { list })
    : t('directories.referenced.deactivateInstead', { list })
}

/** A directory refusal as one line, falling back to the generic mapping.
 *
 * The single entry point a panel needs: it words the FR-39 refusal when that is
 * what happened and the ordinary translated line otherwise, so no call site has
 * to know which refusals carry references.
 */
export function directoryErrorMessage(error: unknown, t: TFunction): string {
  const refusal = referencedRefusal(error)
  if (refusal) return referencedMessage(refusal, t)
  return t(describeProblem(error).detailKey)
}
